import React, { useEffect, useMemo, useCallback } from 'react';
import { AnalyticSearchRequestDto } from 'src/generated/services/TFinancialApi';
import { ColumnHeaderProvider, ColumnHeader, useColumnHeaderContext } from 'src/presentations/components/molecules/table/columnHeader';
import { calculateFilterCount } from 'src/presentations/components/molecules/table/columnHeader/utils/calculateFilterCount';
import { useAnalyticsColumnHeader } from './hooks/useAnalyticsColumnHeader';

type TableType = 'SHIPMENTS' | 'INVOICES' | 'CHARGES';

export interface AnalyticsColumnHeaderProps {
  column: any;
  accessorKey: string;
  filterSearchRequest: AnalyticSearchRequestDto;
  initialSelectedValues: string[];
  drawerFilterValue?: string[]; // Base filter value from drawer (to exclude from badge count)
  onFilter?: (columnId: string, selectedValues: string[]) => void;
  onSort?: (method: string, fieldName: string) => void;
  customHeader?: React.ReactNode;
}

interface CreateAnalyticsColumnHeaderParams {
  tableType: TableType;
  columnNameMap: Record<string, string>;
}

/**
 * Factory function to create analytics column header components
 * Eliminates code duplication across Shipment, Invoice, and Charge column headers
 */
export const createAnalyticsColumnHeader = ({ tableType, columnNameMap }: CreateAnalyticsColumnHeaderParams) => {
  const ColumnHeaderComponent: React.FC<AnalyticsColumnHeaderProps> = ({
    column,
    accessorKey,
    filterSearchRequest,
    initialSelectedValues,
    drawerFilterValue = [],
    onFilter,
    onSort,
    customHeader,
  }) => {
    const {
      filterItems,
      isLoading,
      isFetchingNextPage,
      hasNextPage,
      fetchNextPage,
      enableFilter,
      setHasOpenedFilter,
      handleSearchChange,
      validSelectedValues,
    } = useAnalyticsColumnHeader({
      tableType,
      accessorKey,
      columnNameMap,
      filterSearchRequest,
      initialSelectedValues,
    });

    // Calculate filter count for badge display - exclude drawer filters
    const filterCount = useMemo(() => {
      // Check if current selection is same as drawer filter
      const isSameAsDrawer =
        drawerFilterValue.length > 0 &&
        initialSelectedValues.length === drawerFilterValue.length &&
        initialSelectedValues.every((val, idx) => val === drawerFilterValue[idx]);

      // If it's the same as drawer filter, don't show badge (count = 0)
      if (isSameAsDrawer) {
        return 0;
      }

      // If filterItems is empty (not loaded yet), use initialSelectedValues.length directly
      // This ensures the counter persists even when switching tabs before the filter dropdown is opened
      if (filterItems.length === 0 && initialSelectedValues.length > 0) {
        return initialSelectedValues.length;
      }
      return calculateFilterCount(filterItems, validSelectedValues);
    }, [filterItems, initialSelectedValues, validSelectedValues, drawerFilterValue]);

    // Memoize sort configuration
    const sortConfig = useMemo(
      () => ({
        ascText: 'Sort A to Z',
        descText: 'Sort Z to A',
        onSort: onSort
          ? (_columnId: string, direction: 'asc' | 'desc') => {
              const method = direction === 'desc' ? 'DESC' : 'ASC';
              onSort(method, accessorKey);
            }
          : undefined,
      }),
      [onSort, accessorKey]
    );

    // Memoize props that are only derived when filtering is enabled
    // This prevents creating new object references on every render
    const filterProps = useMemo(() => {
      if (!enableFilter) return {};

      return {
        items: {
          items: filterItems,
          count: filterCount,
          isLoading: isLoading,
          isLoadingMore: isFetchingNextPage,
          hasMore: hasNextPage ?? false,
          onLoadMore: fetchNextPage,
        },
        filter: {
          initialSelectedValues: validSelectedValues, // Use validSelectedValues instead of initialSelectedValues
          onFilter: onFilter
            ? (_columnId: string, selectedValues: string[]) => {
                onFilter(accessorKey, selectedValues);
              }
            : undefined,
          autoSelect: true,
        },
        search: {
          onChange: handleSearchChange,
        },
      };
    }, [
      enableFilter,
      filterItems,
      filterCount,
      isLoading,
      isFetchingNextPage,
      hasNextPage,
      fetchNextPage,
      validSelectedValues, // Changed from initialSelectedValues
      onFilter,
      accessorKey,
      handleSearchChange,
    ]);

    return (
      <ColumnHeaderProvider column={column} sort={sortConfig} {...filterProps}>
        <ColumnHeaderInternal customHeader={customHeader} onOpen={useCallback(() => setHasOpenedFilter(true), [setHasOpenedFilter])} />
      </ColumnHeaderProvider>
    );
  };

  return ColumnHeaderComponent;
};

// Internal component that can access context to trigger lazy load
const ColumnHeaderInternal: React.FC<{ customHeader?: React.ReactNode; onOpen: () => void }> = ({ customHeader, onOpen }) => {
  const { popover } = useColumnHeaderContext();

  // Trigger lazy load when popover opens
  useEffect(() => {
    if (popover.isOpen) {
      onOpen();
    }
  }, [popover.isOpen, onOpen]);

  return <ColumnHeader customHeader={customHeader} />;
};
